import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Download, Trash2, Eye, FileText,
  CheckCircle2, AlertCircle, Users, Clock,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import HelpersManager from "@/components/HelpersManager";
import HistoryPanel from "@/components/HistoryPanel";
import ProfileAudit from "@/components/ProfileAudit";
import ParentActivityPanel from "@/components/ParentActivityPanel";
import MyProposalsList from "@/components/MyProposalsList";

/**
 * Privacy Dashboard — single surface for the user's data control:
 *   • Account snapshot
 *   • Consent log (every accepted version, with timestamps)
 *   • Per-profile actions: export JSON, delete profile
 *   • Account-level actions: link to Settings danger zone
 *   • Reserved sections (helpers, communication history) marked
 *     "coming soon" — built in Blocks 4 + 5.
 *
 * This page is intentionally read-heavy; the heavy lifting (delete profile,
 * etc.) reuses existing endpoints, no new destructive routes here.
 */

const CONSENT_LABELS = {
  terms_of_service: "Terms of Service",
  privacy_policy: "Privacy Policy",
  demo_safety: "Demo Safety Notice",
  photo_upload_first_use: "Photo upload — first-use",
  voice_recording_first_use: "Voice recording — first-use",
  communication_history_opt_in: "Communication history opt-in",
  child_profile_photo_first_use: "Child profile photos — first-use",
  child_profile_voice_first_use: "Child profile voice — first-use",
  therapist_invite_redeem: "Therapist invite — redeemed",
  sponsored_pass_activation: "Sponsored pass — activated",
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function PrivacyMe() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [consents, setConsents] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useSEO({
    title: "Your privacy dashboard",
    description:
      "Review and manage every consent, export, and deletion option for your SonSpeak account.",
    path: "/privacy/me",
    noindex: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/consent/me"),
          api.get("/profiles"),
        ]);
        setConsents(c.data || []);
        setProfiles(p.data || []);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exportProfile = async (p) => {
    try {
      const { data } = await api.get(`/profiles/${p.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sonspeak-${p.name.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Note: board deletion moved to ManageBoard → Parental settings →
  // Danger zone (Defect-batch-2 #4, 2026-07-04). This page keeps
  // export only.

  return (
    <div className="min-h-screen dotted-bg" data-testid="privacy-me-page">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center gap-3 bg-[color:var(--cream)]">
        <button
          type="button"
          onClick={() => nav(-1)}
          className="pill-btn"
          data-testid="privacy-me-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <div className="display text-xs uppercase tracking-[0.25em] text-[color:var(--coral-deep)] font-bold flex items-center gap-1">
            <ShieldCheck size={12} /> Your privacy
          </div>
          <h1 className="display text-2xl sm:text-3xl font-extrabold leading-tight">
            Privacy dashboard
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-8 sm:py-10 space-y-6">
        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3" data-testid="privacy-me-error">
            {error}
          </div>
        )}

        {/* Account snapshot */}
        <section className="brutal-card bg-white p-5">
          <h2 className="display text-xl font-extrabold mb-3">Your account</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[color:var(--ink-soft)] text-xs uppercase tracking-[0.18em] font-bold">Email</div>
              <div className="font-mono">{user?.email || "—"}</div>
            </div>
            <div>
              <div className="text-[color:var(--ink-soft)] text-xs uppercase tracking-[0.18em] font-bold">Role</div>
              <div className="font-mono">{user?.role || "—"}</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/settings" className="pill-btn" data-testid="privacy-me-settings">
              <FileText size={14} /> Settings & danger zone
            </Link>
            <Link to="/demo-safety" className="pill-btn" data-testid="privacy-me-demo-safety">
              <Eye size={14} /> Safety & privacy notice
            </Link>
            <Link to="/privacy" className="pill-btn" data-testid="privacy-me-policy">
              <ShieldCheck size={14} /> Full privacy policy
            </Link>
          </div>
        </section>

        {/* Consent log */}
        <section className="brutal-card bg-white p-5" data-testid="privacy-me-consents">
          <div className="flex items-center justify-between mb-3">
            <h2 className="display text-xl font-extrabold">Consents you've accepted</h2>
            <span className="text-xs text-[color:var(--ink-soft)]">{consents.length} entries</span>
          </div>
          {loading ? (
            <div className="text-[color:var(--ink-soft)]">Loading…</div>
          ) : consents.length === 0 ? (
            <div className="text-[color:var(--ink-soft)] text-sm">
              No consents recorded yet. They appear here the first time you accept a notice
              (photo upload, voice recording, etc.).
            </div>
          ) : (
            <ul className="space-y-2">
              {consents.map((c) => (
                <li
                  key={c.id}
                  className="flex items-start gap-3 px-3 py-2 rounded-xl border-[2px] border-[color:var(--ink)] bg-[color:var(--paper-muted)]"
                  data-testid={`consent-row-${c.consent_type}`}
                >
                  <CheckCircle2 size={16} className="text-[#3A8B3F] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="display font-bold truncate">
                      {CONSENT_LABELS[c.consent_type] || c.consent_type}
                    </div>
                    <div className="text-xs text-[color:var(--ink-soft)] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={10} /> {fmtTs(c.accepted_at)}
                      </span>
                      <span>v{c.version}</span>
                      {c.profile_id && <span>profile {c.profile_id.slice(0, 8)}…</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Profiles — export / delete */}
        <section className="brutal-card bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="display text-xl font-extrabold">Your communication boards</h2>
            <span className="text-xs text-[color:var(--ink-soft)]">{profiles.length}</span>
          </div>
          {loading ? (
            <div className="text-[color:var(--ink-soft)]">Loading…</div>
          ) : profiles.length === 0 ? (
            <div className="text-[color:var(--ink-soft)] text-sm">
              You have no profiles yet. <Link to="/profiles" className="underline font-bold">Create one</Link>.
            </div>
          ) : (
            <ul className="space-y-2">
              {profiles.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl border-[2px] border-[color:var(--ink)] bg-[color:var(--cream-deep)]"
                  data-testid={`privacy-profile-${p.id}`}
                >
                  <span
                    className="w-9 h-9 rounded-xl border-[2px] border-[color:var(--ink)] flex items-center justify-center text-xl"
                    style={{ background: p.color }}
                    aria-hidden
                  >
                    {p.avatar}
                  </span>
                  <span className="flex-1 min-w-0 display font-bold truncate">{p.name}</span>
                  <button
                    type="button"
                    className="pill-btn"
                    onClick={() => exportProfile(p)}
                    data-testid={`privacy-export-${p.id}`}
                    aria-label={`Export ${p.name}`}
                  >
                    <Download size={14} /> Export
                  </button>
                  {/* Board deletion moved to ManageBoard → Parental settings →
                      Danger zone (Defect-batch-2 #4, 2026-07-04). Nothing
                      destructive lives outside Parental Controls. */}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-[color:var(--ink-soft)] mt-3">
            Export downloads a full JSON copy (categories, tiles, custom photos, audio).
            To delete a board, open Manage Board → Parental settings → Danger zone.
          </p>
        </section>

        {/* Helpers — invite/list/revoke per profile (Block 4) */}
        <section className="brutal-card bg-white p-5" data-testid="privacy-me-helpers">
          <div className="flex items-center gap-2 mb-3">
            <Users size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-extrabold">People who can help</h2>
            <span className="ml-auto text-xs text-[color:var(--ink-soft)]">
              {profiles.length} board{profiles.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3 leading-relaxed">
            Invite a grandparent, co-parent, or therapist to support a board with
            the permissions you choose. Helpers never see communication history
            unless you explicitly grant it. Revoke any time.
          </p>
          {loading ? (
            <div className="text-[color:var(--ink-soft)] text-sm">Loading…</div>
          ) : profiles.length === 0 ? (
            <div className="text-sm text-[color:var(--ink-soft)] italic">
              Create a profile first, then invite helpers.
            </div>
          ) : (
            profiles.map((p) => <HelpersManager key={p.id} profile={p} />)
          )}
        </section>

        {/* My proposals — helper-side view of the proposals THEY submitted */}
        <section className="brutal-card bg-white p-5" data-testid="privacy-me-my-proposals">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-extrabold">Tile changes you've suggested</h2>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3 leading-relaxed">
            If you support someone else's board (as a therapist, school staff,
            doctor, or second caregiver), every tile change you propose lives
            here with its current decision status.
          </p>
          <MyProposalsList />
        </section>

        {/* Communication history — opt-in per profile (Block 5) */}
        <section className="brutal-card bg-white p-5" data-testid="privacy-me-history">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-extrabold">Communication history</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.18em] bg-[color:var(--gold)] border-[2px] border-[color:var(--ink)] rounded-full px-2 py-0.5 font-extrabold">Opt-in</span>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3 leading-relaxed">
            Off by default. Turn it on per profile to log taps for review,
            therapist sharing (with explicit <code>view_history</code> permission),
            or saving favorite phrases. Delete or export at any time.
          </p>
          {loading ? (
            <div className="text-[color:var(--ink-soft)] text-sm">Loading…</div>
          ) : profiles.length === 0 ? (
            <div className="text-sm text-[color:var(--ink-soft)] italic">
              Create a profile first.
            </div>
          ) : (
            profiles
              .filter((p) => !p.helper)  // Owner-only — helpers see history via the board if they have the perm
              .map((p) => <HistoryPanel key={p.id} profile={p} />)
          )}
        </section>

        {/* Activity audit log per profile — Block 6 */}
        <section className="brutal-card bg-white p-5" data-testid="privacy-me-audit">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-extrabold">Activity log</h2>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3 leading-relaxed">
            See who did what on each board you own. Helper invites, accepts,
            permission changes, and history views are all logged with a
            timestamp so you can verify activity matches your expectations.
          </p>
          {loading ? (
            <div className="text-[color:var(--ink-soft)] text-sm">Loading…</div>
          ) : profiles.filter((p) => !p.helper).length === 0 ? (
            <div className="text-sm text-[color:var(--ink-soft)] italic">
              Create a profile first.
            </div>
          ) : (
            profiles
              .filter((p) => !p.helper)
              .map((p) => <ProfileAudit key={p.id} profile={p} />)
          )}
        </section>

        {/* Parent-facing audit: who else has touched your child's profile */}
        <section className="space-y-3 mb-6">
          <ParentActivityPanel />
        </section>

        <div className="text-center text-xs text-[color:var(--ink-soft)] pt-4">
          Communication is a human right. Your data is yours — always.
        </div>
      </main>
    </div>
  );
}
